// src/pages/Settings.tsx
import React, { memo, useCallback, useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Stack,
  Divider,
  Card,
  CardContent,
  Grid,
  Avatar,
  IconButton,
} from '@mui/material';
import { useNotification } from '../contexts/NotificationContext';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import LogoutIcon from '@mui/icons-material/Logout';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../hooks/useAuth';
import { UserSession } from '../types/auth';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../components/common/ImageUpload';
import authService from '../services/authService';
import TabPanel from '../components/common/TabPanel';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { authState, deleteAccount, getUserSessions, logoutAll, fetchUser } = useAuth();
  const { success, error: showError } = useNotification();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Profile images state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Notifications state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [eventReminders, setEventReminders] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);

  // Pagination state
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await deleteAccount();
      success(t('settings.messages.accountDeleted'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('settings.messages.deleteAccountFailed'));
    } finally {
      setDeleteLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1 && sessions.length === 0) {
      fetchSessions();
    }
  }, [sessions.length]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const fetchedSessions = await getUserSessions();
      setSessions(fetchedSessions);
    } catch (err: any) {
      showError(err.response?.data?.detail || t('settings.messages.fetchSessionsFailed'));
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    const fetchProfileImages = async () => {
      setLoadingProfile(true);
      try {
        const profileData = await authService.getMyProfile();
        setAvatarUrl(profileData.profile?.avatar_url || null);
        setCoverUrl(profileData.profile?.cover_url || null);
      } catch (err) {
        console.error('Error fetching profile images:', err);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfileImages();
  }, []);

  const handleAvatarUpload = async (file: File, onProgress: (progress: number) => void) => {
    const result = await authService.uploadAvatar(file, onProgress);
    setAvatarUrl(result.avatar_url);
    await fetchUser();
    success(t('settings.messages.avatarUpdated'));
  };

  const handleAvatarDelete = async () => {
    await authService.deleteAvatar();
    setAvatarUrl(null);
    await fetchUser();
  };

  const handleCoverUpload = async (file: File, onProgress: (progress: number) => void) => {
    const result = await authService.uploadCover(file, onProgress);
    setCoverUrl(result.cover_url);
    success(t('settings.messages.coverUpdated'));
  };

  const handleCoverDelete = async () => {
    await authService.deleteCover();
    setCoverUrl(null);
  };

  const handleLogoutAll = async () => {
    try {
      await logoutAll();
      success(t('settings.messages.logoutAllSuccess'));
    } catch (err: any) {
      showError(err.response?.data?.detail || t('settings.messages.logoutAllFailed'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parseUserAgent = (ua: string | undefined | null) => {
    if (!ua) return { browser: t('settings.sessions.unknownBrowser'), icon: '💻' };
    if (ua.includes('Chrome')) return { browser: 'Chrome', icon: '🌐' };
    if (ua.includes('Firefox')) return { browser: 'Firefox', icon: '🦊' };
    if (ua.includes('Safari')) return { browser: 'Safari', icon: '🧭' };
    if (ua.includes('Edge')) return { browser: 'Edge', icon: '🌊' };
    return { browser: t('settings.sessions.browser'), icon: '💻' };
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
          <SettingsIcon sx={{ color: '#2563eb', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            {t('settings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {/* Tabs */}
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="settings tabs"
          sx={{
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 56,
              '&.Mui-selected': { color: '#2563eb' },
            },
            '& .MuiTabs-indicator': { bgcolor: '#2563eb' },
          }}
        >
          <Tab icon={<PersonIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('settings.tabs.profile')} />
          <Tab icon={<DevicesIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('settings.tabs.sessions')} />
          <Tab icon={<NotificationsIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('settings.tabs.notifications')} />
        </Tabs>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            {/* Profile Images Section */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                  <PhotoCameraIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('settings.profile.images.title')}
                </Typography>
              </Stack>

              {loadingProfile ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                        {t('settings.profile.images.avatar')}
                      </Typography>
                      <ImageUpload
                        currentImageUrl={avatarUrl}
                        onUpload={handleAvatarUpload}
                        onDelete={handleAvatarDelete}
                        maxSizeMB={5}
                        label=""
                        helperText={t('settings.profile.images.avatarHelper')}
                        shape="circle"
                        width={120}
                        height={120}
                      />
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={8}>
                    <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                        {t('settings.profile.images.cover')}
                      </Typography>
                      <ImageUpload
                        currentImageUrl={coverUrl}
                        onUpload={handleCoverUpload}
                        onDelete={handleCoverDelete}
                        maxSizeMB={10}
                        label=""
                        helperText={t('settings.profile.images.coverHelper')}
                        shape="rectangle"
                        width={320}
                        height={140}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Profile Information Section */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 1 }}>
                  <BadgeIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('settings.profile.title')}
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ width: 48, height: 48, bgcolor: '#2563eb' }}>
                        {authState.user?.name?.charAt(0).toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('settings.profile.fullName')}</Typography>
                        <Typography variant="body1" fontWeight={600} color="#1e293b">
                          {authState.user?.name || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                        <EmailIcon sx={{ color: '#64748b', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('settings.profile.email')}</Typography>
                        <Typography variant="body1" fontWeight={500} color="#1e293b">
                          {authState.user?.email || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ p: 1.5, bgcolor: authState.user?.role === 'SuperAdmin' ? '#dcfce7' : '#fef3c7', borderRadius: 1 }}>
                        <SecurityIcon sx={{ color: authState.user?.role === 'SuperAdmin' ? '#16a34a' : '#d97706', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('settings.profile.role')}</Typography>
                        <Typography variant="body1" fontWeight={600} color={authState.user?.role === 'SuperAdmin' ? '#16a34a' : '#d97706'} sx={{ textTransform: 'capitalize' }}>
                          {authState.user?.role || '-'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Danger Zone */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                  <WarningAmberIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#dc2626">
                  {t('settings.dangerZone.title')}
                </Typography>
              </Stack>

              <Card sx={{ border: '1px solid #fecaca', boxShadow: 'none', bgcolor: '#fef2f2' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                    <Box>
                      <Typography variant="body1" fontWeight={600} color="#1e293b">
                        {t('settings.dangerZone.deleteMyAccount')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.dangerZone.deleteWarning')}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => setOpenDeleteDialog(true)}
                      sx={{ borderRadius: 1, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, whiteSpace: 'nowrap' }}
                    >
                      {t('settings.dangerZone.deleteAccount')}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* Sessions Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                  <DevicesIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="#1e293b">
                    {t('settings.sessions.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.sessions.activeCount', { count: sessions.length })}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogoutAll}
                sx={{ borderRadius: 1 }}
              >
                {t('settings.sessions.logoutAll')}
              </Button>
            </Stack>

            {loadingSessions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : sessions.length > 0 ? (
              <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('settings.sessions.device')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('settings.sessions.ipAddress')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('settings.sessions.createdAt')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('settings.sessions.expiresAt')}</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('settings.sessions.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((session) => {
                          const { browser, icon } = parseUserAgent(session.user_agent);
                          return (
                            <TableRow
                              key={session.id}
                              sx={{
                                bgcolor: session.is_current ? '#f0fdf4' : 'inherit',
                                '&:hover': { bgcolor: session.is_current ? '#dcfce7' : '#f8fafc' },
                              }}
                            >
                              <TableCell sx={{ py: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <Box sx={{ fontSize: 20 }}>{icon}</Box>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500} color="#1e293b">
                                      {browser}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {session.user_agent}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" fontWeight={500} color="#1e293b">
                                  {session.ip_address}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(session.created_at)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(session.expires_at)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                {session.is_current ? (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#dcfce7', borderRadius: 0.5 }}>
                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                                    <Typography variant="caption" fontWeight={600} color="#16a34a">
                                      {t('settings.sessions.currentSession')}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={sessions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t('settings.sessions.rowsPerPage')}
                  sx={{ borderTop: '1px solid #e2e8f0' }}
                />
              </Card>
            ) : (
              <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ py: 8, textAlign: 'center' }}>
                  <Box sx={{ width: 64, height: 64, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <DevicesIcon sx={{ fontSize: 32, color: '#64748b' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.sessions.noSessions')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: 1 }}>
                <NotificationsIcon sx={{ color: '#d97706', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#1e293b">
                {t('settings.notifications.title')}
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {/* Email Notifications */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                          <EmailIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="#1e293b">
                            {t('settings.notifications.email')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.notifications.emailDescription')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Switch
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2563eb' },
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Push Notifications */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                          <NotificationsIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="#1e293b">
                            {t('settings.notifications.push')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.notifications.pushDescription')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Switch
                        checked={pushNotifications}
                        onChange={(e) => setPushNotifications(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#7c3aed' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7c3aed' },
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Event Reminders */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 1 }}>
                          <AccessTimeIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="#1e293b">
                            {t('settings.notifications.eventReminders')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.notifications.eventRemindersDescription')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Switch
                        checked={eventReminders}
                        onChange={(e) => setEventReminders(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16a34a' },
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Weekly Digest */}
              <Grid item xs={12} md={6}>
                <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
                          <EmailIcon sx={{ color: '#d97706', fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="#1e293b">
                            {t('settings.notifications.weeklyDigest')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.notifications.weeklyDigestDescription')}
                          </Typography>
                        </Box>
                      </Stack>
                      <Switch
                        checked={weeklyDigest}
                        onChange={(e) => setWeeklyDigest(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#d97706' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d97706' },
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Button variant="contained" sx={{ borderRadius: 1, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
                {t('settings.notifications.savePreferences')}
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Card>

      {/* Delete Account Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !deleteLoading && setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ p: 3, bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1.5, bgcolor: '#fee2e2', borderRadius: 1 }}>
                  <WarningAmberIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="#dc2626">
                    {t('settings.deleteDialog.title')}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setOpenDeleteDialog(false)} size="small" disabled={deleteLoading}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {t('settings.deleteDialog.message')}
          </Typography>
          <Box sx={{ mt: 3, p: 2, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
            <Typography variant="body2" color="#dc2626" fontWeight={500}>
              {t('settings.deleteDialog.permanentlyDelete')}
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, color: '#64748b' }}>
              <li><Typography variant="body2">{t('settings.deleteDialog.deleteItems.profile')}</Typography></li>
              <li><Typography variant="body2">{t('settings.deleteDialog.deleteItems.posts')}</Typography></li>
              <li><Typography variant="body2">{t('settings.deleteDialog.deleteItems.subscriptions')}</Typography></li>
            </ul>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleteLoading} sx={{ borderRadius: 1 }}>
            {t('settings.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{ borderRadius: 1, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
          >
            {deleteLoading ? <CircularProgress size={20} color="inherit" /> : t('settings.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(Settings);
